import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {findDOMNode} from 'react-dom';
import Controls from '../Controls/Controls';
import Calendar from '@homeaway/react-calendar-base';
import determineIfMobile from '@homeaway/react-determine-if-mobile';
import throttle from 'lodash.throttle';

class NavCalendarMonth extends Component {
    static propTypes = {
        /** Number of months to show in a desktop horizontal list. */
        numMonthsToDisplay: PropTypes.number,
        /** Number of months to advance/retreat per next/previous button click. Falls back to 1 on mobile. */
        numMonthsToAdvance: PropTypes.number,
        /** Number of columns to use before breaking to a new line. Defaults to numMonthsToDisplay*/
        numColumns: PropTypes.number,
        /** There are four different mobile behaviors (2 are only for use in the DateRangePicker with the affiliates program), 1) scrolling - switches to a vertical scrolling list. Currently capped at 24 months. 2) single - collapses to a single month. 4) single-affiliates - special single month mode for affiliates program (fits within 392px height). 5) double-affiliates - special double month mode for affiliates program (fits within 392px height). */
        mobileBehavior: PropTypes.oneOf(['scrolling', 'single', 'single-affiliates', 'double-affiliates']),
        /** Default 480, Point at which mobile vs desktop views are switched. Anything below or equal to this number will result in mobile view. */
        mobileBreakpoint: PropTypes.number,
        /** Force mobile view. This is useful for server-side rendering when you know if it is mobile or not. It helps prevent react's server/client mismatch warnings. */
        isMobile: PropTypes.bool,
        /** Zero-based index representing the month. This is only necessary if you want to force the calendar to a specific month. By default, displays the month of any selected date or range. If nothing is selected, defaults to today's month. */
        month: PropTypes.number,
        /** Number representing the year, e.g. 2016, . This is only necessary if you want to force the calendar to a specific month. By default, displays the month of any selected date or range. If nothing is selected, defaults to today's month. */
        year: PropTypes.number,
        /** Minimum allowable date before which the prev button will be disabled; scrollable mode uses month and year props. */
        minDate: PropTypes.instanceOf(Date),
        /** Maximum allowable date after which the next button will be disabled; scrollable mode stops loading more months. */
        maxDate: PropTypes.instanceOf(Date),
        /** onNextClicked({month, year}) Triggered when the 'Next' button is clicked, passes the new month/year. */
        onNextClicked: PropTypes.func,
        /** onPrevClicked({month, year}) Triggered when the 'Prev' button is clicked, passes the new month/year. */
        onPrevClicked: PropTypes.func,
        /** By default, the calender elements are given a data-wdio attribute of "calendar-controls" and "calender".
        If you have more than one on a page you can add a prefix to those two strings by using this property. */
        wdioPrefix: PropTypes.string,
        /** A moniker or BCP-47 style language tag string. Defaults to 'en-US'. */
        locale: PropTypes.string,
        /** Flag that specifies whether year has to be displayed first e.g. ja-JP*/
        displayYearFirst: PropTypes.bool,
        /** Optional labels object to override default locale-driven labels */
        labels: PropTypes.shape({
            /** To suppport locales that need a year label e.g. ja-JP*/
            yearLabel: PropTypes.string,
            monthNames: PropTypes.arrayOf(PropTypes.string),
            shortMonthNames: PropTypes.arrayOf(PropTypes.string),
            dayNames: PropTypes.arrayOf(PropTypes.string),
            shortDayNames: PropTypes.arrayOf(PropTypes.string)
        }),
        /** Object containing translations for the current locale. It is assumed that the parent app will prepare and provide the i18n object matching the shape seen here. */
        i18n: PropTypes.shape({
            ariaLabelPrevButton: PropTypes.string,
            ariaLabelNextButton: PropTypes.string
        }),
        /** Callback to notify parent when there is an update (due to next/previous being clicked) and the focusable elements within the component need to be refreshed. */
        onUpdateFocusable: PropTypes.func
    }

    static defaultProps = {
        numMonthsToDisplay: 2,
        numMonthsToAdvance: 1,
        mobileBehavior: 'single',
        onNextClicked: () => {},
        onPrevClicked: () => {},
        minDate: new Date(),
        maxDate: null,
        displayYearFirst: false,
        labels: {
            yearLabel: ''
        },
        i18n: {
            ariaLabelPrevButton: 'Previous month',
            ariaLabelNextButton: 'Next month'
        },
        onUpdateFocusable: () => {}
    }

    constructor(props) {
        super(props);

        const {month, year} = this.getMonthYear(props);
        const {canAdvance, canRetreat} = this.getControlsState(month, year);

        // When mobile mode is "scrolling", it takes too long to render a lot of months up front.
        // Instead we are using an infinite scrolling technique. We start by rendering a single month,
        // then listen for scroll events and load additional months as we approach the end of the scroll
        // list. If there is a selection, we want to scroll to the month with the selection. In order for
        // that to work, we may need to render more than 1 month at the start. getInitialNumMonths() determines
        // how many months to load at the start.
        const loadedMonths = this.getInitialNumMonths(month, year, props.selectedDate, props.selectionStart, props.scrollToMonth);
        const maxLoadableMonths = props.maxDate ? this.getMaxNumMonths(month, year, props.maxDate) : null;

        this.state = {
            month,
            year,
            controls: true,
            loadedMonths,
            maxLoadableMonths,
            canAdvance,
            canRetreat,
            direction: null,
            column: null,
            activeMonth: null,
            refresh: false
        };

        this.lazyLoadHandler = throttle(this.lazyLoadHandler.bind(this), 80);
    }

    componentDidMount() {
        this.setupInfiniteScrolling();
    }

    componentWillReceiveProps(nextProps) {
        // If mobileBehavior changes, set the date back to the original.
        // This prevents a bug that is likely only seen in dev, but whatevs.
        if (nextProps.mobileBehavior !== this.props.mobileBehavior) {
            const {month, year} = this.getMonthYear({...this.props, ...nextProps});

            this.setState({
                month,
                year
            });
        }

        // If month changes and we have a year, update state.
        if (nextProps.month !== this.props.month && (nextProps.year || this.props.year)) {
            this.setState({
                month: nextProps.month,
                year: nextProps.year || this.props.year
            });
        }

        // If year changes and we have a month, update state.
        if (nextProps.year !== this.props.year && (nextProps.month >= 0 || this.props.month >= 0)) {
            this.setState({
                month: nextProps.month >= 0 ? nextProps.month : this.props.month,
                year: nextProps.year
            });
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const {canAdvance, canRetreat} = this.state;

        if (canAdvance !== prevState.canAdvance || canRetreat !== prevState.canRetreat) {
            this.props.onUpdateFocusable();
        }
    }

    componentWillUnmount() {
        this.scrollParent.removeEventListener('scroll', this.lazyLoadHandler);
    }

    getInitialNumMonths(month, year, selectedDate, selectionStart, scrollToMonth) {
        let numMonths = 2;
        const selected = selectedDate || selectionStart || '';

        if (selected && scrollToMonth) {
            const selectedMonth = selected.split('-')[1];
            const selectedYear = selected.split('-')[0];
            const selDate = new Date(selectedYear, selectedMonth, 1);
            const monthDate = new Date(year, month, 1);

            // In certain circumstances this can return a negative number.
            //  1) selection date is in the past
            //  2) consumer is hardwiring the month/year (if not, all is well)
            //  3) has to be in mobile view with mode to "scrolling"
            // It is not immediately obvious what to do in these situations, and since we
            // do not have a use case that fits the scenario above, we'll punt on it for now.
            return selDate.getMonth() - monthDate.getMonth() + (12 * (selDate.getFullYear() - monthDate.getFullYear())) + 1;
        }

        return numMonths;
    }

    getMaxNumMonths(month, year, maxDate) {
        // Get the maximum number of months to load, including start and end months
        let numMonths;
        const monthDate = new Date(year, month, 1);
        numMonths = (maxDate.getFullYear() - monthDate.getFullYear()) * 12;
        numMonths -= monthDate.getMonth();
        numMonths += maxDate.getMonth() + 1;
        return numMonths <= 0 ? 0 : numMonths;
    }

    isWithinBounds(month, year, boundaryDate, minOrMax) {
        // Normalize the min/max Date and current Date so we can compare the 1st of each month
        // When advancing, offset the number of months so maxDate stops in last column
        // Once the current Date tries to exceed the min/max Date, return false
        const isMin = minOrMax === 'min';
        const offset = this.props.isMobile || isMin ? 0 : this.props.numMonthsToDisplay - 1;
        const monthDate = new Date(year, month + offset, 1);
        const boundaryMonthDate = new Date(boundaryDate.getFullYear(), boundaryDate.getMonth(), 1);
        const monthDiff = isMin ? monthDate - boundaryMonthDate : boundaryMonthDate - monthDate;

        if (monthDiff <= 0) {
            return false;
        }

        return true;
    }

    getControlsState(month, year) {
        const {minDate, maxDate} = this.props;
        const canRetreat = minDate ? this.isWithinBounds(month, year, minDate, 'min') : true;
        const canAdvance = maxDate ? this.isWithinBounds(month, year, maxDate, 'max') : true;

        return {canAdvance, canRetreat};
    }

    // Initialize what is needed for infinite scrolling.
    setupInfiniteScrolling() {
        try {
            this.elem = findDOMNode(this);

            // Determine what element (or window) scrolls
            this.scrollParent = this.getScrollParent(this.elem);
            if (this.elem === this.scrollParent) {
                this.elem = this.elem.firstChild;
            }

            // Listen for scroll events
            if (this.props.isMobile && this.props.mobileBehavior === 'scrolling') {
                this.scrollParent.addEventListener('scroll', this.lazyLoadHandler);
            }
        } catch (e) {
            // Used to catch errors during unit testing. Should not happen
            // in browser.
        }
    }

    // Utility to get the scrolling element. We can't guarantee that the window
    // is what is scrolling. The datepickers, for example, scroll within a div.
    getScrollParent(element) {
        let style = window.getComputedStyle(element);
        const excludeStaticParent = style.position === 'absolute';
        const overflowRegex = /(auto|scroll)/;

        if (style.position === 'fixed') {
            return window;
        }

        while (element) {
            style = window.getComputedStyle(element);
            if (excludeStaticParent && style.position === 'static') {
                continue;
            }
            if (overflowRegex.test(style.overflow + style.overflowY)) {
                return element;
            }
            element = element.parentElement;
        }

        return window;
    }

    // Throttled call that loads new months if we approach the end of the list
    lazyLoadHandler() {
        const {loadedMonths, maxLoadableMonths} = this.state;
        const elemBottom = this.elem.getBoundingClientRect().bottom;
        const viewPortHeight = this.scrollParent.clientHeight || this.scrollParent.innerHeight;
        const diffBuffer = viewPortHeight * 0.8;

        if (elemBottom - diffBuffer <= viewPortHeight) {
            if (loadedMonths < maxLoadableMonths || maxLoadableMonths === null) {
                this.setState({loadedMonths: loadedMonths + 1});
            }
        }
    }

    getMonthYear(props) {
        // If month and year were passed in, use those
        if ((props.month || props.month === 0) && props.year) {
            return {month: props.month, year: props.year};
        }

        // If there is a selection (range or single), use that
        const dateString = props.selectionStart || props.selectedDate;

        if (dateString) {
            const dateElems = dateString.split('-');
            return {month: +dateElems[1] - 1, year: +dateElems[0]};
        }

        // Otherwise init the calendar with today's month
        const today = new Date();
        return {month: today.getMonth(), year: today.getFullYear()};
    }

    addMonthsToMonth(year, month, num) {
        const date = new Date(year, month, '01');
        date.setMonth(date.getMonth() + num);
        return {year: date.getFullYear(), month: date.getMonth()};
    }

    handlePrevClicked = () => {
        const {minDate, isMobile, onPrevClicked} = this.props;
        const numMonthsToAdvance = isMobile ? 1 : this.props.numMonthsToAdvance;
        let {month, year} = this.addMonthsToMonth(this.state.year, this.state.month, -numMonthsToAdvance);
        const {canAdvance, canRetreat} = this.getControlsState(month, year);

        // If target date is before min date, set date to minDate
        if (canRetreat === false) {
            month = minDate.getMonth();
            year = minDate.getFullYear();
        }

        this.setState({month, year, canAdvance, canRetreat});
        onPrevClicked({month, year});
    }

    handleNextClicked = () => {
        const {maxDate, isMobile, onNextClicked} = this.props;
        const offset = isMobile ? 0 : this.props.numMonthsToDisplay - 1;
        const numMonthsToAdvance = this.props.isMobile ? 1 : this.props.numMonthsToAdvance;
        let {month, year} = this.addMonthsToMonth(this.state.year, this.state.month, numMonthsToAdvance);
        const {canAdvance, canRetreat} = this.getControlsState(month, year);

        // If target date is after max date, set date to maxDate
        if (canAdvance === false) {
            month = maxDate.getMonth() - offset;
            year = maxDate.getFullYear();
        }

        this.setState({month, year, canAdvance, canRetreat});
        onNextClicked({month, year});
    }

    // If the user is navigating the calendar days via arrow keys and not using the prev/next buttons,
    // then a callback is triggered within calendar-base to notify us when we need to move to the previous or next month.
    handleArrowNavigation = (direction, column) => {
        const {numMonthsToDisplay, isMobile, numMonthsToAdvance} = this.props;
        const {canAdvance, canRetreat} = this.state;
        let numMonthsToAdv = isMobile ? 1 : numMonthsToAdvance;

        // The right arrow key was used while on the last day of a last visible month,
        // so we'll fake the next button being click by calling handleNextClicked()
        if (canAdvance && direction === 'next') {
            this.setState({
                direction,
                activeMonth: (numMonthsToDisplay - numMonthsToAdv - 1),
                refresh: !this.state.refresh
            });
            this.handleNextClicked();
        // The left arrow key was used while on the first day of the first visible month,
        // so we'll fake a prev button being clicked by calling handlePrevClicked()
        } else if (canRetreat && direction === 'prev') {
            this.setState({
                direction,
                activeMonth: (numMonthsToDisplay - (numMonthsToDisplay - numMonthsToAdv)),
                refresh: !this.state.refresh
            });
            this.handlePrevClicked();
        // The up arrow key was used while on the first day of the first visible month,
        // so we'll fake a prev button being clicked by calling handlePrevClicked()
        } else if (canRetreat && direction === 'up') {
            this.setState({
                direction,
                column,
                activeMonth: (numMonthsToDisplay - (numMonthsToDisplay - numMonthsToAdv)),
                refresh: !this.state.refresh
            });
            this.handlePrevClicked();
        // The down arrow key was used while on the last day of a last visible month,
        // so we'll fake the next button being click by calling handleNextClicked()
        } else if (canAdvance && direction === 'down') {
            this.setState({
                direction,
                column,
                activeMonth: (numMonthsToDisplay - numMonthsToAdv - 1),
                refresh: !this.state.refresh
            });
            this.handleNextClicked();
        }
    }

    getNumColumns() {
        return this.props.numColumns || this.props.numMonthsToDisplay;
    }

    render() {
        // Some of these are itemized to strip them out. ...rest is passed into calendar
        const {isMobile, mobileBehavior, onNextClicked, onPrevClicked, numMonthsToDisplay, className, wdioPrefix, locale, labels, i18n, ...rest} = this.props; // eslint-disable-line no-unused-vars
        let numMonths = this.props.numMonthsToDisplay;
        let numColumns = this.getNumColumns();
        const isSingle = this.props.mobileBehavior === 'single' || this.props.mobileBehavior === 'single-affiliates';
        const isDouble = this.props.mobileBehavior === 'double-affiliates';
        let hasControls = !this.props.isMobile || isSingle || isDouble;

        if (isMobile) {
            if (isSingle) {
                numMonths = 1;
                numColumns = 1;
            } else if (isDouble) {
                numMonths = 2;
                numColumns = 2;
            } else {
                numMonths = this.state.loadedMonths;
                numColumns = 1;
            }
        }

        return (
            <Controls
                className={className}
                enabled={hasControls}
                isMobile={isMobile}
                mobileBehavior={mobileBehavior}
                numMonths={numMonths}
                onPrevClicked={this.handlePrevClicked}
                onNextClicked={this.handleNextClicked}
                canAdvance={this.state.canAdvance}
                canRetreat={this.state.canRetreat}
                wdioPrefix={wdioPrefix}
                i18n={i18n}
            >
                <Calendar
                    {...rest}
                    month={this.state.month}
                    year={this.state.year}
                    numMonthsToDisplay={numMonths}
                    numColumns={numColumns}
                    padWeeks
                    wdioPrefix={wdioPrefix}
                    locale={locale}
                    labels={labels}
                    onArrowNavigation={this.handleArrowNavigation}
                    direction={this.state.direction}
                    column={this.state.column}
                    activeMonth={this.state.activeMonth}
                    refresh={this.state.refresh}
                    displayYearFirst={this.props.displayYearFirst}
                />
            </Controls>
        );
    }
}

export default determineIfMobile(NavCalendarMonth);
